import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { monday } from './platformSpecific/monday';
import { ThemeProvider } from 'styled-components';
import { LoadingPage } from './components';
import { HostPlatform } from './config/hostPlatform';
import { GlobalContext, useGlobalStateValue } from './context/GlobalState';
import { Routes } from './hoc';
import {
  AppInitializationContext,
  firebaseInit,
} from './services/firebaseInit';
import { blackTheme, darkTheme, GlobalStyle, lightTheme } from './styles';
import { initTracking } from './tracking';
import { errorNoticeDefault } from './utils/notice';
import { NoLicenseErrorPage } from './containers/ErrorPage';
import { AdminPage } from './containers/AdminPage';

const version = process.env.REACT_APP_VERSION;

const themes = {
  dark: darkTheme,
  light: lightTheme,
};

function isEventDarkMode(event) {
  const bgColor = event.detail['palette-neutral-0'];
  return bgColor.split(',')[0] !== '255';
}

function useMondayTheme() {
  const shouldLoad = HostPlatform.isMonday();
  const [currentTheme, setCurrentTheme] = useState('light');

  useEffect(() => {
    function loadMonday() {
      monday.listen('context', (response: any) => {
        setCurrentTheme(response.data.theme);
      });
    }

    if (shouldLoad) {
      loadMonday();
    }
  }, [shouldLoad]);

  return shouldLoad
    ? {
        theme: themes[currentTheme] || blackTheme,
      }
    : undefined;
}

function useAzureTheme() {
  const shouldLoad = HostPlatform.isAzure();
  const [currentTheme, setCurrentTheme] = useState('light');

  useEffect(() => {
    function themeListenerHandler(event) {
      setCurrentTheme(isEventDarkMode(event) ? 'dark' : 'light');
    }

    function loadAzure() {
      window.addEventListener('themeApplied', themeListenerHandler);
    }

    if (shouldLoad) {
      loadAzure();
    }

    return () => {
      window.removeEventListener('themeApplied', themeListenerHandler);
    };
  }, [shouldLoad]);

  return shouldLoad
    ? {
        theme: themes[currentTheme] || blackTheme,
      }
    : undefined;
}

function useJiraTheme() {
  return HostPlatform.isJira() ? { theme: lightTheme } : undefined;
}

function usePlatformTheme() {
  const azureTheme = useAzureTheme();
  const mondayTheme = useMondayTheme();
  const jiraTheme = useJiraTheme();
  const themeFound = azureTheme || mondayTheme || jiraTheme;
  if (!themeFound) {
    console.warn('No theme has been found. Fallback to lightTheme.');
    return { theme: lightTheme };
  }
  return themeFound;
}

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: 'production',
    integrations: [new BrowserTracing()],
    release: version,
    attachStacktrace: true,
    tracesSampleRate: 1,
  });
} else {
  console.warn('Sentry was not loaded!');
}

let platformInitializingPromise: Promise<AppInitializationContext>;

async function initPlatform(): Promise<AppInitializationContext> {
  const hostPlatform = await HostPlatform.init();

  const result = await firebaseInit(hostPlatform);
  await initTracking();
  return result.appInitializationContext;
}

function useInitializePlatformQuery() {
  const { onLoading, isLoading } = useGlobalStateValue();
  return useQuery<AppInitializationContext, Error>(
    ['platform'],
    () => {
      if (platformInitializingPromise) {
        return platformInitializingPromise;
      }
      onLoading(true);
      platformInitializingPromise = initPlatform();
      return platformInitializingPromise;
    },
    {
      enabled: !isLoading && !platformInitializingPromise,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
      refetchOnMount: !platformInitializingPromise,
      onSettled() {
        onLoading(false);
      },
      onError(error) {
        Sentry.captureException(error);
        errorNoticeDefault(error);
      },
    }
  );
}

type AppProperties = {
  isAdminEntryPoint: boolean;
};

function App({ isAdminEntryPoint }: AppProperties) {
  const value = useGlobalStateValue();
  const {
    error,
    isSuccess,
    isLoading,
    data: appInitializationContext,
  } = useInitializePlatformQuery();
  const { theme } = usePlatformTheme();
  const licenseIsActive = appInitializationContext?.subscription.isActive;

  return (
    <GlobalContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <GlobalStyle theme={theme} />
        <Sentry.ErrorBoundary fallback={<p>an error has occurred</p>}>
          {isAdminEntryPoint && isSuccess && (
            <AdminPage subscription={appInitializationContext.subscription} />
          )}
          {!isAdminEntryPoint &&
            ((isLoading && <LoadingPage />) ||
              (isSuccess && licenseIsActive && <Routes />) ||
              (isSuccess && !licenseIsActive && (
                <NoLicenseErrorPage
                  subscription={appInitializationContext.subscription}
                />
              )))}
          {error && <p>{error.message}</p>}
          <ReactQueryDevtools initialIsOpen={false} />
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </GlobalContext.Provider>
  );
}

export default Sentry.withProfiler(App);
